
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Filter, FilterX } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { GENRES, ENVIRONMENTS, STORY_LENGTHS } from "@/lib/story-constants";
import { THEMES } from "@/components/story-setup/ThemeSection";
import { useLanguage } from "@/hooks/useLanguage";
import { Separator } from "@/components/ui/separator";

interface StoryFiltersProps {
  filters: {
    genre: string[];
    environment: string[];
    storyLength: string[];
    themes: string[];
  };
  onFilterChange: (filters: {
    genre: string[];
    environment: string[];
    storyLength: string[];
    themes: string[];
  }) => void;
}

const StoryFilters = ({ filters, onFilterChange }: StoryFiltersProps) => {
  const { t } = useLanguage();
  const [localFilters, setLocalFilters] = useState(filters);
  
  const handleGenreToggle = (genre: string) => {
    const newGenres = localFilters.genre.includes(genre)
      ? localFilters.genre.filter(g => g !== genre)
      : [...localFilters.genre, genre];
    setLocalFilters({ ...localFilters, genre: newGenres });
  };

  const handleEnvironmentToggle = (env: string) => {
    const newEnvironments = localFilters.environment.includes(env)
      ? localFilters.environment.filter(e => e !== env)
      : [...localFilters.environment, env];
    setLocalFilters({ ...localFilters, environment: newEnvironments });
  };

  const handleLengthToggle = (length: string) => {
    const newLengths = localFilters.storyLength.includes(length)
      ? localFilters.storyLength.filter(l => l !== length)
      : [...localFilters.storyLength, length];
    setLocalFilters({ ...localFilters, storyLength: newLengths });
  };

  const handleThemeToggle = (theme: string) => {
    const newThemes = localFilters.themes.includes(theme)
      ? localFilters.themes.filter(t => t !== theme)
      : [...localFilters.themes, theme];
    setLocalFilters({ ...localFilters, themes: newThemes });
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      genre: [],
      environment: [],
      storyLength: [],
      themes: [],
    };
    setLocalFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  const activeFiltersCount = 
    localFilters.genre.length + 
    localFilters.environment.length + 
    localFilters.storyLength.length +
    localFilters.themes.length;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button 
          variant="outline" 
          className="bg-white/10 text-white w-full justify-start"
        >
          <Filter className="mr-2 h-4 w-4" />
          {t('dashboard.filters.title')}
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" className="ml-2">
              {activeFiltersCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{t('dashboard.filters.title')}</SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)]">
          <div className="py-6 space-y-8">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-primary">{t('storySetup.basicInfo.genre')}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {GENRES.map((genre) => (
                  <Button
                    key={genre}
                    variant={localFilters.genre.includes(genre) ? "default" : "outline"}
                    className="w-full text-sm py-1 px-3 h-8"
                    onClick={() => handleGenreToggle(genre)}
                  >
                    {t(`storySetup.genres.${genre}`)}
                  </Button>
                ))}
              </div>
              <Separator className="my-4" />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-primary">{t('storySetup.basicInfo.environment')}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {ENVIRONMENTS.map((env) => (
                  <Button
                    key={env}
                    variant={localFilters.environment.includes(env) ? "default" : "outline"}
                    className="w-full text-sm py-1 px-3 h-8"
                    onClick={() => handleEnvironmentToggle(env)}
                  >
                    {t(`storySetup.environments.${env}`)}
                  </Button>
                ))}
              </div>
              <Separator className="my-4" />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-primary">{t('storySetup.basicInfo.storyLength')}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {STORY_LENGTHS.map((length) => (
                  <Button
                    key={length}
                    variant={localFilters.storyLength.includes(length) ? "default" : "outline"}
                    className="w-full text-sm py-1 px-3 h-8"
                    onClick={() => handleLengthToggle(length)}
                  >
                    {t(`storySetup.storyLengths.${length}`)}
                  </Button>
                ))}
              </div>
              <Separator className="my-4" />
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-primary">{t('storySetup.themeSection.title')}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {THEMES.map((theme) => (
                  <Button
                    key={theme}
                    variant={localFilters.themes.includes(theme) ? "default" : "outline"}
                    className="w-full text-sm py-1 px-3 h-8"
                    onClick={() => handleThemeToggle(theme)}
                  >
                    {t(`storySetup.themeOptions.${theme}`)}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="flex justify-between mt-6">
          <Button variant="outline" onClick={handleResetFilters}>
            <FilterX className="mr-2 h-4 w-4" />
            {t('dashboard.filters.reset')}
          </Button>
          <SheetClose asChild>
            <Button onClick={handleApplyFilters}>
              {t('dashboard.filters.apply')}
            </Button>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default StoryFilters;
